const entries = [
  {
    version: "1.4.0",
    date: "2026-04-27",
    title: "Cloudflare Named Tunnels — Stable Per-Developer URLs",
    items: [
      "New: gigsmart.rocks zone delegated to Cloudflare; Universal SSL covers *.gigsmart.rocks. Each dev gets a permanent <username>.gigsmart.rocks hostname for their local API.",
      "gs-hive setup-tunnel rewritten — runs `cloudflared tunnel login`, creates the tunnel, calls `tunnel route dns` to auto-create the CNAME, and persists tunnel UUID + credentials path to ~/.gs-hive/config.json.",
      "gs-hive run --tunnel now uses --credentials-file when available — no dependency on cert.pem at runtime.",
      "Replaces the flaky `*.trycloudflare.com` quick-tunnel path. APK can now be cached across runs because the URL never changes.",
      "Removed obsolete CLOUDFLARE_SETUP_WALKTHROUGH.md + tunnel.ops.gigsmart.com NS terraform stub (we went a different route).",
      "Updated NAMED_TUNNEL_SETUP.md to reflect the actual gigsmart.rocks-based architecture, with manual fallback steps and troubleshooting.",
    ],
  },
  {
    version: "1.3.1",
    date: "2026-04-27",
    title: "Auto-migrate on pod startup",
    items: [
      "Fix: v1.3.0 added install_log + install_error columns to device_claims, but our deploy flow doesn't run migrate.ts automatically — the new pod started against the old schema and every Drizzle query that touched device_claims threw `column \"install_log\" does not exist`, 500'ing the dashboard.",
      "server.ts now calls runMigrations() before app.listen — every pod boot reapplies schema.sql (idempotent, every statement uses IF NOT EXISTS / IF EXISTS guards).",
      "Migrate failures are logged loudly but don't crash startup — a transient AlloyDB blip can't crash-loop the pod.",
    ],
  },
  {
    version: "1.3.0",
    date: "2026-04-27",
    title: "Hive Self-Service: Agent + Install Logs in the UI",
    items: [
      "New: HostDetail surfaces a live 'Recent agent events' panel — agent ships its last 100 stdout/stderr lines in each heartbeat (~15s); UI dedupes via sequence numbers and streams new lines via SSE. No more SSH to /var/log/device-hive-agent.log to debug.",
      "New: DeviceDetail shows the full 'APK install log' for the most recent claim — every adb command, download progress, and Play Protect dialog is captured, persisted to JSONB on device_claims, and rendered with level filters + search.",
      "New: shared LogPanel component — auto-scroll-to-bottom, info/warn/error level filters, free-text search, dark console theme.",
      "New: GET /api/hosts/:id/logs (in-memory ring buffer, last 1000 lines per host) and GET /api/devices/:id/latest-install-log endpoints.",
      "Schema: device_claims gains install_log JSONB and install_error TEXT columns (idempotent migration).",
      "Agent: tees console.log/error into an in-memory ring buffer that's drained into each heartbeat — captures everything written by every agent module.",
      "Agent: APK installer captures stdout/stderr per adb subprocess as structured log lines instead of a single error string.",
    ],
  },
  {
    version: "1.2.3",
    date: "2026-04-24",
    title: "Curl-able Host Install + /docs Fix",
    items: [
      "New: `/install` endpoint — `curl -fsSL https://device-hive.ops.gigsmart.com/install | bash` provisions a fresh Mac Mini end-to-end. No git clone needed first.",
      "Hosts page now has a prominent click-to-copy curl command panel — adding a host is a paste-and-done flow.",
      "Setup guide modal updated to advertise the curl command (was: git clone instructions).",
      "host-setup.sh header comment updated with the new curl flow.",
      "Fix: `/docs` was 500'ing because openapi.yaml + scripts/ weren't COPY'd into the production image. Dockerfile updated.",
    ],
  },
  {
    version: "1.2.2",
    date: "2026-04-24",
    title: "Documentation Sweep + Pre-flight Polish",
    items: [
      "New: `/docs` page with full OpenAPI 3.1 reference (37 endpoints, 27 schemas) rendered via Scalar API Reference — auth-gated, lazy-loaded so non-visitors don't pay the bundle cost",
      "New: README for ops/device-hive — entry point for engineers new to the backend",
      "New: README for ops/gs-hive — entry point for CLI users",
      "New: ARCHITECTURE.md — system topology + sequence diagrams (developer run, CI run, agent heartbeat, SSE fan-out)",
      "New: RUNBOOK.md — 10 symptom → diagnosis → fix sections for the most common operational failures",
      "New: DEPLOYMENT.md — exact build/push/rollout procedure; corrects stale doc that had wrong namespace and registry",
      "New: DISASTER_RECOVERY.md — honest assessment of current backup story (AlloyDB PITR ✓, GCS artifacts versioning needed, no drill has run)",
      "New: SLOS.md — proposed SLIs/SLOs for when observability gets wired up",
      "New: CONTRIBUTING.md — onboarding guide with concrete 'edit here to X' pointers",
      "Infra: GCS artifacts bucket lifecycle updated to 180-day STANDARD → NEARLINE → COLDLINE tiers (was: 30-day hard DELETE); object versioning enabled; force_destroy disabled",
      "Internal: fixed dashboard/dist .gitignore patterns so webpack chunks don't pollute git diffs",
    ],
  },
  {
    version: "1.2.1",
    date: "2026-04-24",
    title: "Named Tunnels + Reliability + Audit",
    items: [
      "gs-hive: `setup-tunnel` wizard for Cloudflare named tunnels — stable URLs instead of flaky trycloudflare.com quick tunnels (pending Cloudflare billing approval for org rollout)",
      "gs-hive: `--tunnel` now auto-uses named tunnel when configured, falls back to quick tunnel otherwise",
      "gs-hive: tunnel setup now respawns cloudflared on dead DNS URLs (known Cloudflare quirk); retry up to 3 times automatically",
      "gs-hive: preflight now uses OS DNS resolver (not c-ares) and retries on 502/503/530 during Cloudflare edge warmup",
      "gs-hive: `GS_HIVE_DEBUG=1` for verbose tunnel/preflight diagnostics",
      "Staged Terraform for `tunnel.ops.gigsmart.com` NS delegation (ops/gs-hive/CLOUDFLARE_SETUP_WALKTHROUGH.md)",
      "Internal: completed full audit of Hive + gs-hive covering security, docs, DX, self-help — 41 findings prioritized; v1.3 'Hive Self-Service' milestone planned",
    ],
  },
  {
    version: "1.2.0",
    date: "2026-04-23",
    title: "gs-hive CLI Reliability + Job Lookup Fuzzy Search",
    items: [
      "Job Lookup — fuzzy search across pipeline ID, job ID, commit ref, and user (e.g. type `cdownard` to filter to your runs)",
      "Job Lookup — redesigned as a list view sorted by most recent; click any row to open Test Run Detail",
      "CLI token runs now get tagged with the caller's email prefix (`cli-cdownard-<ts>`) so they're filterable per-user",
      "`gs-hive run --api-url <url>` — build APK pointing at a custom URL (e.g. a cloudflared tunnel for local-API testing)",
      "`gs-hive run` feature paths now resolve against `mobile/apps/<app>/` as a fallback; invalid paths error out instead of silently falling back to the full suite (fixes 2x-per-scenario duplication bug)",
      "`gs-hive run` marks each device card `passed`/`failed` when its cucumber process exits — no more cards stuck on \"Running\"",
      "`gs-hive run` ADB connect now rejects on `failed to authenticate` with a clear \"tap Always allow\" message",
      "`gs-hive run` one bad device no longer aborts the entire run — uses `Promise.allSettled`",
      "`gs-hive run` `q` (quit) now cancels in-flight gsutil artifact uploads immediately instead of waiting for all ~90 files",
      "`gs-hive run` suppresses expected 404s on early-quit artifact attach — no more scary NOT_FOUND stack traces",
      "Release via CLI token now puts devices in `needs_cleanup` (matches CI endpoints) — agent uninstalls app + returns to home screen on next heartbeat",
      "Test Run Detail — executions pre-populate steps from pickle.steps as `not_run` so failed-Before scenarios still show their intended step list instead of being empty",
      "Test Run artifact GCS paths slugified to ASCII (`✗ Sign out` → `sign-out`) — fixes videos/screenshots that returned 404 due to double-encoding",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-04-22",
    title: "Initial Release",
    items: [
      "Fleet Overview with live SSE device status updates",
      "Test Run Detail with 3-column layout — scenarios, live steps, video playback",
      "Step-seeking in videos — click a step to jump to that point",
      "Batch device claiming — claim up to N devices, get whatever's available",
      "APK install pipeline — CI uploads to GCS, agent installs locally over USB",
      "Staggered test start — workers launch as devices become ready",
      "Host Management with setup guide modal",
      "Device Detail with health info, battery, ADB TCP connect address",
      "Job Lookup — search by GitLab CI job ID",
      "Live Activity feed — watch test steps execute in real-time",
      "Attempt grouping — retries shown separately with pass/fail counts",
      "Device cleanup lifecycle — uninstall apps, restore animations after each run",
      "Google OAuth with @gigsmart.com domain restriction",
      "SSE connection status indicator (Live/Disconnected)",
      "Mac Mini host auto-provisioning script",
      "Samsung A-series device support with USB quirks handling",
      "Zero-downtime deployment with haproxy sticky sessions",
    ],
  },
];

export default function Changelog() {
  return (
    <div>
      <h1 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", marginBottom: "var(--space-xl)" }}>
        Changelog
      </h1>
      {entries.map((entry) => (
        <div
          key={entry.version}
          style={{
            background: "var(--color-surface)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-card)",
            padding: "var(--space-lg)",
            marginBottom: "var(--space-lg)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-md)", marginBottom: "var(--space-md)" }}>
            <span style={{
              background: "var(--color-primary)",
              color: "#fff",
              padding: "2px 10px",
              borderRadius: "var(--radius-pill)",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
            }}>
              v{entry.version}
            </span>
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>{entry.date}</span>
          </div>
          <h2 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-sm)" }}>{entry.title}</h2>
          <ul style={{ paddingLeft: 20, fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", lineHeight: 1.8 }}>
            {entry.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
